// Puerto de la bandeja del proveedor (PP-34). La vista habla con esto y no con
// fetch: en los tests se reemplaza por un doble y en la app por http.ts.
//
// Mismo contrato que usa el sistema de un proveedor integrado por API
// (PLAN-ASIGNACION-PROVEEDOR §10): el portal no tiene endpoints propios.
// Todo metodo recibe la planta seleccionada; no hay tenant global.
import type {
  AssignTripRequest,
  AssignTripResponse,
  OfferRejectReason,
  ProviderOffersResponse,
  ProviderTrip,
  ProviderTripDetailResponse,
  ProviderTripsResponse,
} from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import type { FleetSite } from "../fleet/port.js";

export type RequestsSite = FleetSite;

export interface RequestsPort {
  /** Viajes de un dia operativo (`date` ausente: el de hoy segun la planta). */
  list(session: PortalSession, site: RequestsSite, date?: string): Promise<ProviderTripsResponse>;
  /**
   * Ofertas vivas (por responder o por asignar) de cualquier dia operativo
   * (PP-34b): el plazo corre aunque el viaje no sea del dia que se mira.
   */
  offers(session: PortalSession, site: RequestsSite): Promise<ProviderOffersResponse>;
  /** Hoja de ruta: paradas, horarios y cantidad de pasajeros. Lectura auditada por la API. */
  detail(session: PortalSession, site: RequestsSite, tripId: string): Promise<ProviderTripDetailResponse>;
  accept(session: PortalSession, site: RequestsSite, tripId: string, offerId: number): Promise<ProviderTrip>;
  reject(session: PortalSession, site: RequestsSite, tripId: string, offerId: number, reason: OfferRejectReason): Promise<void>;
  assign(session: PortalSession, site: RequestsSite, tripId: string, body: AssignTripRequest): Promise<AssignTripResponse>;
}

/**
 * Por que fallo una llamada, sin el cuerpo remoto: la UI elige el copy por
 * este codigo y nunca pinta el `detail` de la API.
 * - access: 401 o 403 (sin habilitacion, o vehiculo o conductor de otro proveedor).
 * - not_found: 404 (el viaje ya no es visible para la empresa).
 * - offer_not_open, offer_not_yours, trip_not_assignable, roster_entry_unknown:
 *   los 409 tipados de la asignacion (PP-33).
 * - conflict: otro 409.
 * - validation: 400.
 * - unavailable: 5xx, red o respuesta que viola el contrato.
 */
export type RequestsErrorKind =
  | "access"
  | "not_found"
  | "offer_not_open"
  | "offer_not_yours"
  | "trip_not_assignable"
  | "roster_entry_unknown"
  | "conflict"
  | "validation"
  | "unavailable";

export class RequestsError extends Error {
  constructor(readonly kind: RequestsErrorKind) {
    super(`requests_${kind}`);
  }
}
